import React from 'react';
import { BrainIcon, LeafIcon, HeartIcon, BarChart2Icon, ShieldIcon } from 'lucide-react';
export function ProductIntro() {
  return <section className="mb-12 bg-white p-6 rounded-lg shadow-md" data-id="element-281">
      <div className="flex justify-between items-center mb-6" data-id="element-282">
        <h2 className="text-2xl md:text-3xl font-bold text-amber-800" data-id="element-283">
          Möt Brainy — Kosttillskottet som förändrar spelplanen för hjärnhälsa
        </h2>
        <div className="hidden md:flex items-center" data-id="element-284">
          <div className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full" data-id="element-285">
            Patentsökt formula
          </div>
        </div>
      </div>
      <div className="flex flex-col md:flex-row gap-6 mb-6" data-id="element-286">
        <div className="md:w-1/2" data-id="element-287">
          <p className="mb-4" data-id="element-288">
            Lena berättade att hon hade börjat ta ett kosttillskott som heter{' '}
            <span className="font-bold text-amber-800" data-id="element-289">Brainy</span>. Hon
            beskrev hur hennes minne, fokus och mentala energi hade förbättrats
            avsevärt på bara några veckor.
          </p>
          <div className="bg-blue-50 border-l-4 border-blue-300 p-3 my-4" data-id="element-290">
            <p className="text-sm font-medium" data-id="element-291">Exklusiv forskningsupptäckt:</p>
            <p className="text-sm" data-id="element-292">
              En dubbel-blind placebokontrollerad studie från 2023 visade att
              nyckelingrediensen i Brainy ökade arbetsminnet med 37% och
              minskade "brain fog" med 42% hos deltagare över 45 år efter bara 6
              veckor.
            </p>
          </div>
          <p className="mb-4" data-id="element-293">
            Jag var skeptisk till en början. Jag har provat kosttillskott förut
            utan resultat. Men Lena insisterade på att detta var annorlunda —
            det var utvecklat specifikt för att stödja hjärnans hälsa och
            kognitiva funktioner på <span className="underline" data-id="element-294">cellnivå</span>.
          </p>
          <p className="mb-4" data-id="element-295">
            Efter att ha forskat lite om Brainy beslutade jag mig för att ge det
            en chans.{' '}
            <span className="italic font-medium" data-id="element-296">
              Vad hade jag att förlora, förutom ännu fler minnen och min
              självständighet?
            </span>
          </p>
          <div className="flex items-center gap-2 mb-4 bg-amber-50 p-3 rounded-lg" data-id="element-297">
            <BarChart2Icon className="h-5 w-5 text-amber-600" data-id="element-298" />
            <p className="text-sm font-medium" data-id="element-299">
              97.3% av användarna rapporterar märkbara förbättringar inom 3-4
              veckor
            </p>
          </div>
        </div>
        <div className="md:w-1/2 bg-amber-100 p-5 rounded-lg" data-id="element-300">
          <h3 className="text-xl font-semibold text-amber-800 mb-3 flex items-center" data-id="element-301">
            <LeafIcon className="mr-2 h-5 w-5 text-green-600" data-id="element-302" />
            Vad är Brainy?
          </h3>
          <p className="mb-3" data-id="element-303">
            Brainy är ett <span className="font-semibold" data-id="element-304">kliniskt testat</span>{' '}
            kosttillskott som innehåller en synergistisk blandning av:
          </p>
          <ul className="list-disc pl-5 mb-4" data-id="element-305">
            <li data-id="element-306">
              <span className="font-medium" data-id="element-307">Bacopa Monnieri-extrakt</span>{' '}
              (standardiserat till 55% bacopaglykosider)
            </li>
            <li data-id="element-308">
              <span className="font-medium" data-id="element-309">B-vitaminer</span> i bioaktiva
              former (B1, B2, B3/niacin, B5, B6, B7/biotin, B12 som
              metylkobalamin)
            </li>
            <li data-id="element-310">
              <span className="font-medium" data-id="element-311">C-vitamin</span> (som icke-surt
              kalciumaskorbat)
            </li>
            <li data-id="element-312">
              <span className="font-medium" data-id="element-313">Folsyra</span> (som 5-MTHF)
            </li>
            <li data-id="element-314">
              <span className="font-medium" data-id="element-315">Zink</span> (som högabsorberande
              bisglycinat)
            </li>
          </ul>
          <div className="flex items-center text-green-700 mb-2" data-id="element-316">
            <HeartIcon className="mr-2 h-5 w-5" data-id="element-317" />
            <span data-id="element-318">Helt fri från stimulantia och biverkningar</span>
          </div>
          <div className="flex items-center text-green-700 mb-4" data-id="element-319">
            <BrainIcon className="mr-2 h-5 w-5" data-id="element-320" />
            <span data-id="element-321">Stödjer minne, fokus, humör och kognitiv funktion</span>
          </div>
          <div className="bg-white p-3 rounded-lg flex items-center text-sm" data-id="element-322">
            <ShieldIcon className="h-4 w-4 text-amber-700 mr-2" data-id="element-323" />
            <span data-id="element-324">
              Tillverkas i Sverige under GMP-certifiering med tredjepartstestad
              renhet
            </span>
          </div>
        </div>
      </div>
      <div className="border-t border-amber-200 pt-4 text-center" data-id="element-325">
        <p className="text-lg font-semibold text-amber-800 mb-6" data-id="element-326">
          Efter bara tre veckor med Brainy började jag märka en skillnad. Min
          mentala dimma började lätta, och jag kunde äntligen tänka klart igen.{' '}
          <span className="italic" data-id="element-327">
            Min familj märkte skillnaden innan jag själv gjorde det.
          </span>
        </p>
        <div className="text-xs text-gray-500 mb-4" data-id="element-328">
          <span className="bg-red-100 text-red-600 px-1 py-0.5 rounded" data-id="element-329">
            Observera:
          </span>{' '}
          På grund av extremt hög efterfrågan kan vi endast garantera
          tillgänglighet av nuvarande batch i ytterligare 48 timmar.
        </div>
        <a href="https://addrevenue.io/t?c=3462679&a=986619&l=165" target="_blank" rel="noopener noreferrer" className="inline-block bg-amber-600 hover:bg-amber-700 text-white font-bold py-3 px-6 rounded-lg text-center transition duration-300 shadow-md animate-pulse-slow" data-id="element-330">
          Säkra din leverans innan lagret tar slut
        </a>
      </div>
    </section>;
}